import { CoreEvent, eventNames, type Events } from '@carvana/core-events';
import { useEffectWithController } from '@carvana/merch-react-hooks';
import { useContext } from 'react';
import { useResolvedOnServer } from '~/context/common/DeferredResolutionContext';
import UserContext, {
  useIsLoggedIn,
  useUserLocation
} from '~/context/common/UserContext';
import useSkipFirstFetchIfResolvedOnServer from '~/hooks/common/useSkipFirstFetchIfResolvedOnServer';
import useFavoritesFetcher from './useFavoritesFetcher';

export default function useGetFavorites() {
  const {
    favoriteChangeFlag,
    favorites: { setLoaded, setData },
    triggerEvent
  } = useContext(UserContext);
  const { favorites: favoritesResolvedOnServer } = useResolvedOnServer();
  const fetchFavorites = useFavoritesFetcher();
  const { zip } = useUserLocation();
  const isLoggedIn = useIsLoggedIn();
  const shouldSkipFetch = useSkipFirstFetchIfResolvedOnServer(
    favoritesResolvedOnServer,
    favoriteChangeFlag
  );

  useEffectWithController(
    controller => {
      if (!isLoggedIn) {
        setLoaded();

        return;
      }

      if (!zip) return;

      function updateFavoritesData(
        payload: Events[typeof eventNames.favorites.mfe.refresh]
      ) {
        if (payload.source === 'app' || !payload.vehicles) return;

        setData(payload.vehicles);
      }

      // The MFE refresh listener below must always register, even when the fetch is skipped.
      if (!shouldSkipFetch(favoriteChangeFlag)) {
        void fetchFavorites(zip, controller);
      }

      CoreEvent.on(eventNames.favorites.mfe.refresh, updateFavoritesData);

      return () => {
        CoreEvent.off(eventNames.favorites.mfe.refresh, updateFavoritesData);
      };
    },
    [
      favoriteChangeFlag,
      fetchFavorites,
      isLoggedIn,
      setData,
      setLoaded,
      shouldSkipFetch,
      triggerEvent,
      zip
    ]
  );
}
